import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ErrorBoundary from "../../components/base/ErrorBoundary";
import { useArticle } from "../../hooks/useArticle";
import { useArticles } from "../../hooks/useArticles";
import { useComments } from "../../hooks/useComments";
import { useReactions } from "../../hooks/useReactions";
import { useSponsors } from "../../hooks/useSponsors";
import { toNewsItem, toNewsItems } from "../../lib/articleAdapter";
import { sanitizeWordHtml, sanitizeInterviewHtml } from "../../lib/sanitizeWordHtml";
import { getShareUrl } from "../../lib/share";
import { displayViewsLabel } from "../../lib/displayViews";
import SEO from "@/components/SEO";

// Logo del medio para el structured data de NewsArticle. Debe coincidir con el
// que declara articulo-og.php: Google compara la entidad del editor entre el
// HTML prerenderizado y el que renderiza React, y una discrepancia la descarta.
const PUBLISHER_LOGO =
	"https://storage.readdy-site.link/project_files/7a6028ad-3552-457e-be8d-c77b0b9450c6/31ae53ac-c509-4654-8250-6af6c39c53e4_android-chrome-512x512.png?v=5e3efa8fc6b42c1bebef2ecc5da5ae1b";

/** Google recorta los titulares a 110 caracteres en los datos estructurados. */
function clampHeadline(text: string, max = 110): string {
	return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

function injectImagesIntoHtml(
	html: string,
	images: string[],
	captions: string[],
	title: string,
): { processedHtml: string; remainingImages: string[] } {
	if (!images.length) return { processedHtml: html, remainingImages: [] };

	const parts = html.split(/(<\/p>)/i);
	let imageIdx = 0;
	let paragraphCount = 0;
	let result = "";

	for (let i = 0; i < parts.length; i++) {
		result += parts[i];
		if (parts[i].toLowerCase() === "</p>") {
			paragraphCount++;
			if (paragraphCount % 2 === 0 && imageIdx < images.length) {
				const safeTitle = title.replace(/"/g, "&quot;");
				const caption = captions[imageIdx]?.trim();
				const captionHtml = caption
					? `<p class="text-slate-400 text-xs mt-2 italic">${caption}</p>`
					: "";
				result += `
          <div class="my-8 text-center">
            <div class="max-w-2xl mx-auto rounded-xl overflow-hidden border border-slate-200 bg-[#0B1F3A]">
              <img src="${images[imageIdx]}" alt="${caption || `Imagen ${imageIdx + 1} - ${safeTitle}`}" class="w-full h-auto object-contain" loading="lazy" />
            </div>
            ${captionHtml}
          </div>`;
				imageIdx++;
			}
		}
	}

	return { processedHtml: result, remainingImages: images.slice(imageIdx) };
}

function buildPlainTextWithImages(
	paragraphs: string[],
	images: string[],
	captions: string[],
	title: string,
): { elements: React.ReactNode[]; remainingImages: string[] } {
	const elements: React.ReactNode[] = [];
	let imageIdx = 0;

	for (let i = 0; i < paragraphs.length; i++) {
		elements.push(
			<p
				key={`p-${i}`}
				className="text-slate-600 text-base leading-relaxed"
				style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
			>
				{paragraphs[i]}
			</p>,
		);

		if ((i + 1) % 2 === 0 && imageIdx < images.length) {
			const caption = captions[imageIdx]?.trim();
			elements.push(
				<div key={`img-${imageIdx}`} className="py-4">
					<div className="max-w-2xl mx-auto rounded-xl overflow-hidden border border-slate-200 bg-[#0B1F3A]">
						<img
							src={images[imageIdx]}
							alt={caption || `Imagen ${imageIdx + 1} - ${title}`}
							className="w-full h-auto object-contain"
							loading="lazy"
						/>
					</div>
					{caption && (
						<p className="text-center text-slate-400 text-xs mt-2 italic">
							{caption}
						</p>
					)}
				</div>,
			);
			imageIdx++;
		}
	}

	return { elements, remainingImages: images.slice(imageIdx) };
}

function ArticuloContent() {
	const { slug } = useParams<{ slug: string }>();
	const navigate = useNavigate();
	const { article, loading } = useArticle(slug);
	const { articles: allArticles } = useArticles();
	const {
		comments,
		addComment,
		loading: commentsLoading,
	} = useComments(article?.id);
	const { counts, userReactions, toggleReaction } = useReactions(article?.id);
	const { sponsors: articleSponsors } = useSponsors("Articulo");

	const [commentName, setCommentName] = useState("");
	const [commentEmail, setCommentEmail] = useState("");
	const [commentContent, setCommentContent] = useState("");
	const [commentSubmitting, setCommentSubmitting] = useState(false);
	const [commentSuccess, setCommentSuccess] = useState(false);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, [slug]);

	useEffect(() => {
		if (article?.title) {
			document.title = `${article.title} | Aviación Insight RD`;
		}
	}, [article]);

	const handleCommentSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (
			!commentName.trim() ||
			!commentEmail.trim() ||
			!commentContent.trim() ||
			!article
		)
			return;

		setCommentSubmitting(true);
		const result = await addComment(commentName, commentEmail, commentContent);
		setCommentSubmitting(false);

		if (result.success) {
			setCommentName("");
			setCommentEmail("");
			setCommentContent("");
			setCommentSuccess(true);
			setTimeout(() => setCommentSuccess(false), 3000);
		}
	};

	if (loading) {
		return (
			<main
				className="min-h-screen bg-[#F4F7FB]"
				style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
			>
				<Navbar />
				<div className="w-full bg-slate-200 animate-pulse" style={{ aspectRatio: '16/9', maxHeight: '560px' }} />
				<div className="bg-white px-4 md:px-6 py-6 md:py-8">
					<div className="max-w-4xl mx-auto space-y-3">
						<div className="h-6 bg-slate-200 rounded w-3/4 animate-pulse" />
						<div className="h-4 bg-slate-200 rounded w-1/3 animate-pulse" />
					</div>
				</div>
				<div className="max-w-screen-xl mx-auto px-4 md:px-6 py-12">
					<div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
						<div className="lg:col-span-3 space-y-6">
							<div className="h-8 bg-slate-200 rounded w-3/4 animate-pulse" />
							<div className="h-4 bg-slate-200 rounded w-full animate-pulse" />
							<div className="h-4 bg-slate-200 rounded w-5/6 animate-pulse" />
							<div className="h-4 bg-slate-200 rounded w-4/5 animate-pulse" />
						</div>
						<div className="space-y-6">
							<div className="h-40 bg-slate-200 rounded-xl animate-pulse" />
							<div className="h-40 bg-slate-200 rounded-xl animate-pulse" />
						</div>
					</div>
				</div>
				<Footer />
			</main>
		);
	}

	if (!article) {
		return (
			<main className="min-h-screen bg-[#F4F7FB]">
				<Navbar />
				<div className="flex flex-col items-center justify-center min-h-screen gap-4">
					<i className="ri-file-unknow-line text-5xl text-slate-300" />
					<p
						className="text-slate-500 text-lg font-semibold"
						style={{ fontFamily: "'Sora', sans-serif" }}
					>
						Artículo no encontrado
					</p>
					<Link
						to="/actualidad"
						className="px-6 py-3 bg-[#0B1F3A] text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
						style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
					>
						Ver todas las noticias
					</Link>
				</div>
				<Footer />
			</main>
		);
	}

	// Ultra-defensive: ensure every field is a valid value
	const safeArticle = {
		id: article.id ?? 0,
		title: article.title || "",
		excerpt: article.excerpt || "",
		content: article.category === "Entrevistas" || (Array.isArray(article.tags) && article.tags.includes("Entrevistas"))
			? (article.content || "")
			: sanitizeInterviewHtml(article.content || ""),
		category: article.category || "",
		category_color: article.category_color || "bg-blue-500",
		author: article.author || "",
		date: article.date || "",
		read_time: article.read_time || "",
		image: article.image || "",
		featured: article.featured || false,
		slug: article.slug || "",
		tags: Array.isArray(article.tags) ? article.tags : [],
		views: article.views ?? 0,
		is_international: article.is_international || false,
		video_url: (article as any).video_url || "",
		additional_images: Array.isArray((article as any).additional_images) ? (article as any).additional_images : [],
	};

	const siteUrl = import.meta.env.VITE_SITE_URL || "https://aviacioninsightrd.com";
	const articleUrl = `${siteUrl}/articulo/${safeArticle.slug}`;
	const shareUrl = getShareUrl(safeArticle.slug);

	const newsItem = toNewsItem(safeArticle as any);
	const related = toNewsItems(allArticles || [])
		.filter(
			(a) =>
				a.slug !== slug &&
				((Array.isArray(a.tags)
					? a.tags.some((t) => safeArticle.tags?.includes(t))
					: false) ||
					a.category === safeArticle.category),
		)
		.slice(0, 3);

	const moreNews = toNewsItems(allArticles || [])
		.filter((a) => a.slug !== slug)
		.sort((a, b) => (b.views || 0) - (a.views || 0))
		.slice(0, 4);

	const paragraphs = (safeArticle.content || "")
		.split("\n\n")
		.filter((p) => p.trim());
	const safeTags: string[] = safeArticle.tags;
	const safeSponsors = Array.isArray(articleSponsors) ? articleSponsors : [];
	const safeComments = Array.isArray(comments) ? comments : [];
	const safeAdditionalImages: string[] = Array.isArray(safeArticle.additional_images) ? safeArticle.additional_images : [];
	const safeImageCaptions: string[] = Array.isArray((safeArticle as any).image_captions) ? (safeArticle as any).image_captions : [];

	// Process content with inline images
	let contentWithImages: { processedHtml: string; remainingImages: string[] } | null = null;
	let textContentWithImages: { elements: React.ReactNode[]; remainingImages: string[] } | null = null;

	if (safeArticle.content.startsWith('<')) {
		contentWithImages = injectImagesIntoHtml(
			safeArticle.content,
			safeAdditionalImages,
			safeImageCaptions,
			safeArticle.title,
		);
	} else {
		textContentWithImages = buildPlainTextWithImages(
			paragraphs,
			safeAdditionalImages,
			safeImageCaptions,
			safeArticle.title,
		);
	}

	const remainingImages = contentWithImages?.remainingImages ?? textContentWithImages?.remainingImages ?? [];

	return (
		<main
			className="min-h-screen bg-[#F4F7FB]"
			style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
		>
			{safeArticle.title && (
				<SEO
					ogTitle={`${safeArticle.title} | Aviación Insight RD`}
					ogDescription={safeArticle.excerpt}
					ogImage={safeArticle.image || undefined}
					ogUrl={articleUrl}
					ogType="article"
					ogSiteName="Aviación Insight RD"
					ogLocale="es_ES"
					twitterCard="summary_large_image"
					articlePublishedTime={article.created_at || safeArticle.date}
					articleModifiedTime={article.updated_at || article.created_at || safeArticle.date}
					articleSection={safeArticle.category}
					articleAuthor={safeArticle.author || "Carmen Bretón"}
					jsonLdSchemas={[
						{
							"@type": "NewsArticle",
							mainEntityOfPage: {
								"@type": "WebPage",
								"@id": articleUrl,
							},
							url: articleUrl,
							// Google trunca el titular a 110 caracteres en Noticias;
							// mandarlo más largo invalida el dato estructurado.
							headline: clampHeadline(safeArticle.title),
							description: safeArticle.excerpt,
							image: safeArticle.image ? [safeArticle.image] : undefined,
							author: [
								{
									"@type": "Person",
									name: safeArticle.author || "Carmen Bretón",
									url: `${siteUrl}/sobre-mi`,
								},
							],
							publisher: {
								"@type": "NewsMediaOrganization",
								"@id": `${siteUrl}/#organization`,
								name: "Aviación Insight RD",
								url: siteUrl,
								logo: {
									"@type": "ImageObject",
									url: PUBLISHER_LOGO,
									width: 512,
									height: 512,
								},
							},
							datePublished: article.created_at || safeArticle.date,
							dateModified: article.updated_at || article.created_at || safeArticle.date,
							inLanguage: "es",
							isAccessibleForFree: true,
							articleSection: safeArticle.category,
							keywords: (safeArticle.tags || []).join(', '),
						},
						{
							"@type": "BreadcrumbList",
							itemListElement: [
								{ "@type": "ListItem", position: 1, name: "Inicio", item: siteUrl },
								{
									"@type": "ListItem",
									position: 2,
									name: safeArticle.category || "Actualidad",
									item: `${siteUrl}/actualidad`,
								},
								{ "@type": "ListItem", position: 3, name: safeArticle.title, item: articleUrl },
							],
						},
					]}
				/>
			)}
			<Navbar />

				{/* ===== Mobile + Desktop: imagen completa arriba, texto debajo ===== */}
				<div className="w-full bg-[#0B1F3A] pt-16 lg:pt-24">
					<div className="max-w-4xl mx-auto px-4 md:px-6">
						{safeArticle.image ? (
							<img
								src={safeArticle.image}
								alt={safeArticle.title}
								className="w-full h-auto block"
								style={{ maxHeight: 'none' }}
							/>
						) : (
							<div className="w-full bg-slate-300" style={{ aspectRatio: '16/9' }} />
						)}
					</div>
				</div>
				<div className="bg-white px-4 md:px-6 py-6 md:py-8">
					<div className="max-w-4xl mx-auto">
						{/* Breadcrumb */}
						<div className="flex items-center gap-2 text-sm mb-4">
							<Link
								to="/"
								className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
							>
								Inicio
							</Link>
							<i className="ri-arrow-right-s-line text-slate-400" />
							<Link
								to="/actualidad"
								className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
							>
								Actualidad
							</Link>
							<i className="ri-arrow-right-s-line text-slate-400" />
							<span className="text-slate-500 truncate max-w-[120px] md:max-w-xs">
								{safeArticle.category}
							</span>
						</div>
						<div className="flex items-center gap-2 mb-3 flex-wrap">
							<span
								className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full ${safeArticle.category_color}`}
							>
								{safeArticle.category}
							</span>
							{safeArticle.is_international && (
								<span className="inline-flex items-center gap-1 text-blue-600 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100">
									<i className="ri-earth-line" />
									Internacional
								</span>
							)}
						</div>
						<h1
							className="text-[#0B1F3A] font-extrabold leading-tight text-xl sm:text-2xl md:text-3xl"
							style={{ fontFamily: "'Sora', sans-serif" }}
						>
							{safeArticle.title}
						</h1>
						<div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
							<div className="flex items-center gap-1.5">
								<i className="ri-calendar-line text-slate-400" />
								<span>{safeArticle.date}</span>
							</div>
							<div className="flex items-center gap-1.5">
								<i className="ri-eye-line text-slate-400" />
								<span>{displayViewsLabel(safeArticle.views, safeArticle.slug)} lecturas</span>
							</div>
						</div>
					</div>
				</div>

			{/* Content */}
			<div className="max-w-screen-xl mx-auto px-4 md:px-6 py-12">
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
					{/* Article body */}
					<article className="lg:col-span-3">
						{/* Share bar */}
						<div className="flex flex-wrap items-center gap-3 mb-8 pb-6 border-b border-slate-200">
							<span className="text-slate-500 text-sm font-semibold w-full sm:w-auto">
								Compartir:
							</span>
							<button
								onClick={async () => {
									if (navigator.share) {
										try {
											await navigator.share({
												title: safeArticle.title,
												text: safeArticle.excerpt,
												url: shareUrl,
											});
										} catch {
											// User cancelled
										}
									} else {
										try {
											await navigator.clipboard.writeText(shareUrl);
										} catch {
											const textarea = document.createElement("textarea");
											textarea.value = shareUrl;
											textarea.style.position = "fixed";
											textarea.style.opacity = "0";
											document.body.appendChild(textarea);
											textarea.select();
											document.execCommand("copy");
											document.body.removeChild(textarea);
										}
									}
								}}
								className="flex items-center gap-2 px-4 py-2 bg-[#0B1F3A] text-white text-sm font-semibold rounded-lg hover:bg-slate-700 transition-colors cursor-pointer whitespace-nowrap"
							>
								<i className="ri-share-forward-line" /> Compartir
							</button>
						</div>

						{/* Lead */}
						{safeArticle.excerpt && (
							<p
								className="text-[#0B1F3A] text-lg leading-relaxed font-semibold mb-8 border-l-4 border-blue-600 pl-5 whitespace-pre-line"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								{safeArticle.excerpt}
							</p>
						)}

						{/* Video player */}
						{safeArticle.video_url && (
							<div className="mb-8 rounded-2xl overflow-hidden border border-slate-200 bg-[#0B1F3A]">
								{(() => {
									const url = safeArticle.video_url;
									const isYoutube = url.includes("youtube.com") || url.includes("youtu.be");
									if (isYoutube) {
										let embedId = "";
										if (url.includes("v=")) {
											const match = url.match(/v=([a-zA-Z0-9_-]+)/);
											if (match) embedId = match[1];
										} else if (url.includes("youtu.be/")) {
											const match = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
											if (match) embedId = match[1];
										}
										if (embedId) {
											return (
												<iframe
													className="w-full aspect-video"
													src={`https://www.youtube.com/embed/${embedId}`}
													title={safeArticle.title}
													allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
													allowFullScreen
												/>
											);
										}
										}
										return (
											<video
												src={url}
												controls
												className="w-full aspect-video object-contain bg-black"
												preload="metadata"
												poster={safeArticle.image}
											/>
										);
									})()}
								</div>
							)}

							{/* Body content - supports both HTML (rich editor) and plain text */}
							{safeArticle.content.startsWith('<') ? (
								<div
									className="article-body prose prose-slate max-w-none"
									style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
									dangerouslySetInnerHTML={{ __html: contentWithImages!.processedHtml }}
								/>
							) : (
								<div className="space-y-6">
									{textContentWithImages!.elements}
								</div>
							)}

							{/* Remaining images gallery */}
							{remainingImages.length > 0 && (
								<div className="mt-10 pt-6 border-t border-slate-200">
									<h3
										className="text-[#0B1F3A] font-bold text-sm mb-4 flex items-center gap-2"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										<i className="ri-image-line text-blue-500" />
										Galería de imágenes
									</h3>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
										{remainingImages.map((img, idx) => {
											const inlineCount = safeAdditionalImages.length - remainingImages.length;
											const caption = safeImageCaptions[inlineCount + idx]?.trim();
											return (
												<div key={idx} className="space-y-1">
													<div className="rounded-xl overflow-hidden border border-slate-200 bg-[#0B1F3A]">
														<img
															src={img}
															alt={caption || safeArticle.title}
															className="w-full h-auto object-contain"
															loading="lazy"
														/>
													</div>
													{caption && (
														<p className="text-slate-400 text-xs italic">{caption}</p>
													)}
												</div>
											);
										})}
									</div>
								</div>
							)}

							{/* Tags */}
							<div className="mt-10 pt-6 border-t border-slate-200">
								<div className="flex flex-wrap gap-2">
									{[
										...new Set([
											safeArticle.category,
											"Aviación RD",
											"Caribe",
											"Aeronáutica",
											"aviacioninsigthrd",
											...safeTags,
										]),
									]
										.filter(Boolean)
										.map((tag, idx) => (
											<Link
												key={`${tag}-${idx}`}
												to="/actualidad"
												className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap"
											>
												#{tag}
											</Link>
										))}
								</div>
							</div>

							{/* Reactions */}
							<div className="mt-8 bg-white rounded-2xl p-6 border border-slate-100">
								<h3
									className="text-[#0B1F3A] font-bold text-sm mb-4"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									¿Te gustó este artículo?
								</h3>
								<div className="flex flex-wrap items-center gap-3">
									{(["like", "heart", "fire"] as const).map((type) => {
										const isActive = userReactions?.has(type) || false;
										const icons = {
											like: "ri-thumb-up-line",
											heart: "ri-heart-line",
											fire: "ri-fire-line",
										};
										const labels = {
											like: "Me gusta",
											heart: "Me encanta",
											fire: "Increíble",
										};
										const colors = {
											like: "bg-blue-50 text-blue-600 border-blue-200",
											heart: "bg-rose-50 text-rose-600 border-rose-200",
											fire: "bg-orange-50 text-orange-600 border-orange-200",
										};
										const activeColors = {
											like: "bg-blue-600 text-white border-blue-600",
											heart: "bg-rose-600 text-white border-rose-600",
											fire: "bg-orange-600 text-white border-orange-600",
										};
										return (
											<button
												key={type}
												onClick={() => toggleReaction(type)}
												className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${isActive ? activeColors[type] : colors[type]} hover:scale-105`}
											>
												<i className={icons[type]} />
												<span>{labels[type]}</span>
												<span
													className={`text-xs font-bold ${isActive ? "text-white/80" : ""}`}
												>
													{counts?.[type] ?? 0}
												</span>
											</button>
										);
										})}
									</div>
								</div>

								{/* Comments Section */}
								<div className="mt-12">
									<div className="flex items-center gap-3 mb-6">
										<div className="w-1 h-8 bg-[#0B1F3A] rounded-full" />
										<h2
											className="text-[#0B1F3A] font-extrabold text-xl"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											Comentarios ({safeComments.length})
										</h2>
									</div>

									{/* Comment form */}
									<form
										onSubmit={handleCommentSubmit}
										className="bg-white rounded-2xl p-6 border border-slate-100 mb-8"
									>
										<h3
											className="text-[#0B1F3A] font-bold text-sm mb-4"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											Deja tu comentario
										</h3>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
											<input
												type="text"
												placeholder="Tu nombre"
												value={commentName}
												onChange={(e) => setCommentName(e.target.value)}
												className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 transition-colors"
												required
											/>
											<input
												type="email"
												placeholder="Tu correo electrónico"
												value={commentEmail}
												onChange={(e) => setCommentEmail(e.target.value)}
												className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 transition-colors"
												required
											/>
										</div>
										<textarea
											placeholder="Escribe tu comentario..."
											value={commentContent}
											onChange={(e) => setCommentContent(e.target.value)}
											maxLength={500}
											rows={4}
											className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 transition-colors resize-none mb-3"
											required
										/>
										<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
											<span className="text-slate-400 text-xs">
												{commentContent.length}/500 caracteres
											</span>
											<button
												type="submit"
												disabled={commentSubmitting}
												className="px-6 py-2.5 bg-[#0B1F3A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
											>
												{commentSubmitting ? (
													<span className="flex items-center gap-2">
														<i className="ri-loader-4-line animate-spin" />{" "}
														Enviando...
													</span>
												) : (
													<span className="flex items-center gap-2">
														<i className="ri-send-plane-line" /> Enviar comentario
													</span>
												)}
											</button>
										</div>
										{commentSuccess && (
											<div className="mt-3 flex items-center gap-2 text-emerald-600 text-sm">
												<i className="ri-checkbox-circle-line" />
												<span>¡Comentario enviado exitosamente!</span>
											</div>
										)}
									</form>

									{/* Comments list */}
									{commentsLoading ? (
										<div className="space-y-4">
											{[1, 2, 3].map((i) => (
												<div
													key={i}
													className="bg-white rounded-xl p-5 border border-slate-100 animate-pulse"
												>
													<div className="h-4 bg-slate-200 rounded w-1/4 mb-3" />
													<div className="h-3 bg-slate-200 rounded w-full mb-2" />
													<div className="h-3 bg-slate-200 rounded w-3/4" />
												</div>
											))}
										</div>
									) : safeComments.length === 0 ? (
										<div className="text-center py-10 bg-white rounded-2xl border border-slate-100">
											<i className="ri-chat-3-line text-3xl text-slate-300 mb-3 block" />
											<p className="text-slate-500 text-sm">
												Sé el primero en comentar este artículo.
											</p>
										</div>
									) : (
										<div className="space-y-4">
											{safeComments.map((comment) => (
												<div
													key={comment.id ?? Math.random()}
													className="bg-white rounded-xl p-5 border border-slate-100"
												>
													<div className="flex items-center gap-3 mb-3">
														<div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
															<span className="text-blue-600 font-bold text-sm">
																{(comment.name || "?").charAt(0).toUpperCase()}
															</span>
														</div>
														<div>
															<p className="text-[#0B1F3A] font-semibold text-sm">
																{comment.name || "Anónimo"}
															</p>
															<p className="text-slate-400 text-xs">
																{comment.created_at
																	? new Date(comment.created_at).toLocaleDateString(
																			"es-ES",
																			{
																					day: "numeric",
																					month: "short",
																					year: "numeric",
																					hour: "2-digit",
																					minute: "2-digit",
																				},
																			)
																	: ""}
															</p>
														</div>
													</div>
													<p className="text-slate-600 text-sm leading-relaxed">
														{comment.content || ""}
													</p>
												</div>
											))}
										</div>
									)}
								</div>
							</article>

							{/* Sidebar */}
							<aside className="space-y-8">
								{/* Back button */}
								<button
									onClick={() => navigate(-1)}
									className="flex items-center gap-2 text-slate-500 text-sm font-semibold hover:text-slate-700 transition-colors cursor-pointer"
								>
									<i className="ri-arrow-left-line" /> Volver
								</button>

								{/* Related */}
								<div className="bg-white rounded-xl p-5 border border-slate-100">
									<h3
										className="text-[#0B1F3A] font-bold mb-4 flex items-center gap-2"
										style={{ fontFamily: "'Sora', sans-serif", fontSize: "1rem" }}
									>
										<i className="ri-links-line text-blue-500" />
										Artículos relacionados
									</h3>
									{related.length > 0 ? (
										<div className="space-y-4">
											{related.map((item) => (
												<Link
													key={item.id}
													to={`/articulo/${item.slug}`}
													className="flex gap-3 group cursor-pointer"
												>
													<div className="flex-shrink-0 w-16 h-14 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
														<img
															src={item.image}
															alt={item.title}
															className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
														/>
													</div>
													<div className="flex-1 min-w-0">
														<p
															className="text-[#0B1F3A] text-xs font-semibold leading-snug line-clamp-2 group-hover:text-slate-500 transition-colors"
															style={{ fontFamily: "'Sora', sans-serif" }}
														>
															{item.title}
														</p>
														<p className="text-slate-400 text-[10px] mt-1">
															{item.date}
														</p>
													</div>
												</Link>
											))}
										</div>
									) : (
										<p
											className="text-slate-400 text-xs text-center py-3"
											style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
										>
											No hay artículos relacionados aún
										</p>
									)}
								</div>

								{/* Newsletter */}
								<div className="bg-[#0B1F3A] rounded-xl p-5 text-center">
									<i className="ri-mail-send-line text-blue-300 text-2xl mb-3 block" />
									<p
										className="text-white font-bold text-sm mb-2"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										Newsletter Semanal
									</p>
									<p className="text-blue-200/70 text-xs mb-4">
										Recibe las noticias más importantes del sector aéreo de RD y el
										Caribe.
									</p>
									<Link
										to="/#newsletter"
										className="block w-full py-2.5 bg-white text-[#0B1F3A] font-semibold text-sm rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
									>
										Suscribirme
									</Link>
								</div>

								{/* Más leídas */}
								<div className="bg-white rounded-xl p-5 border border-slate-100">
									<h3
										className="text-[#0B1F3A] font-bold mb-4 flex items-center gap-2"
										style={{ fontFamily: "'Sora', sans-serif", fontSize: "1rem" }}
									>
										<i className="ri-fire-line text-orange-500" />
										Más leídas
									</h3>
									<div className="space-y-3">
										{moreNews.map((item, idx) => (
											<Link
												key={item.id}
												to={`/articulo/${item.slug}`}
												className="flex gap-2 group cursor-pointer"
											>
												<span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-slate-100 text-slate-600 rounded text-[10px] font-bold group-hover:bg-slate-200 transition-colors">
													{idx + 1}
												</span>
												<div className="flex-1 min-w-0">
													<p
														className="text-slate-600 text-xs leading-snug line-clamp-2 group-hover:text-[#0B1F3A] transition-colors"
														style={{
															fontFamily: "'Plus Jakarta Sans', sans-serif",
														}}
													>
														{item.title}
													</p>
												</div>
											</Link>
										))}
									</div>
								</div>

								{/* Sponsors */}
								{safeSponsors.length > 0 ? (
									<div className="space-y-4">
										{safeSponsors.map((sponsor) => (
											<a
												key={sponsor.id}
												href={sponsor.website_url || "#"}
												target="_blank"
												rel="nofollow noreferrer"
												className="relative rounded-xl overflow-hidden border border-slate-100 group cursor-pointer hover:-translate-y-0.5 transition-transform block"
											>
												<div className="absolute top-2 left-2 z-10 bg-black/50 text-white text-[9px] font-bold px-2 py-0.5 rounded-full tracking-widest uppercase">
													Publicidad
												</div>
												{sponsor.banner_url ? (
													<img
														src={sponsor.banner_url}
														alt={sponsor.company_name || ""}
														className="w-full h-44 object-contain object-center group-hover:scale-105 transition-transform duration-500"
													/>
												) : (
													<div className="w-full h-44 bg-gradient-to-r from-[#0B1F3A] to-[#1a3a6b] flex items-center justify-center">
														<p className="text-white font-bold text-sm">
															{sponsor.company_name || "Patrocinador"}
														</p>
													</div>
												)}
												<div className="p-3 bg-gradient-to-r from-[#0B1F3A] to-[#1a3a6b]">
													<p
														className="text-white font-bold text-xs"
														style={{ fontFamily: "'Sora', sans-serif" }}
													>
														{sponsor.company_name || "Patrocinador"}
													</p>
													{sponsor.notes && (
														<p
															className="text-blue-200/70 text-[10px] mt-0.5"
															style={{
																fontFamily: "'Plus Jakarta Sans', sans-serif",
															}}
														>
															{sponsor.notes}
														</p>
													)}
												</div>
											</a>
										))}
									</div>
								) : (
									<div className="space-y-4">
										<Link
											to="/publicidad"
											className="relative rounded-xl overflow-hidden border border-dashed border-slate-200 group cursor-pointer hover:border-red-300 hover:bg-red-50/30 transition-all block"
										>
											<div className="h-44 flex flex-col items-center justify-center text-center p-6">
												<div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl mb-3 group-hover:bg-red-100 transition-colors">
													<i className="ri-megaphone-line text-slate-400 text-xl group-hover:text-red-500 transition-colors" />
												</div>
												<p
													className="text-slate-500 font-semibold text-sm"
													style={{ fontFamily: "'Sora', sans-serif" }}
												>
													¿Tu empresa aquí?
												</p>
												<p
													className="text-slate-400 text-xs mt-1"
													style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
												>
													Llega a miles de lectores del sector aeronáutico
												</p>
												<span className="mt-3 inline-flex items-center gap-1 text-red-500 text-[11px] font-semibold">
													Anunciar aquí <i className="ri-arrow-right-line" />
												</span>
											</div>
										</Link>
									</div>
								)}
								</aside>
							</div>

							{/* Related articles grid */}
							<div className="mt-16">
								<div className="flex items-center gap-4 mb-8">
									<div className="w-1 h-8 bg-[#0B1F3A] rounded-full" />
									<h2
										className="text-[#0B1F3A] font-extrabold text-2xl"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										También te puede interesar
									</h2>
								</div>
								{related.length > 0 ? (
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
										{related.map((item) => (
											<Link
												key={item.id}
												to={`/articulo/${item.slug}`}
												className="bg-white rounded-2xl overflow-hidden group cursor-pointer border border-slate-100 hover:border-slate-200 transition-all hover:-translate-y-1 duration-300 block"
											>
												<div className="block h-40 sm:h-48 overflow-hidden bg-[#0B1F3A] relative flex items-center justify-center">
													<img
														src={item.image}
														alt={item.title}
														className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
													/>
												</div>
												<div className="p-4">
													<span
														className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${item.categoryColor}`}
													>
														{item.category}
													</span>
													<h3
														className="text-[#0B1F3A] font-bold text-sm leading-snug line-clamp-2 group-hover:text-slate-600 transition-colors mt-2 mb-2"
														style={{ fontFamily: "'Sora', sans-serif" }}
													>
														{item.title}
													</h3>
													<div className="flex items-center justify-between">
														<span className="text-xs text-slate-400">{item.date}</span>
														<span className="text-xs text-slate-500 font-semibold flex items-center gap-1 group-hover:text-slate-700 transition-colors whitespace-nowrap">
															Leer <i className="ri-arrow-right-line text-xs" />
														</span>
													</div>
												</div>
											</Link>
										))}
									</div>
								) : (
									<div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
										<div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
											<i className="ri-links-line text-slate-400 text-2xl" />
										</div>
										<p
											className="text-slate-400 text-sm"
											style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
										>
											No hay artículos relacionados aún
										</p>
									</div>
								)}
							</div>
						</div>

						<Footer />
					</main>
				);
			}

			export default function ArticuloPage() {
				return (
					<ErrorBoundary>
						<ArticuloContent />
					</ErrorBoundary>
				);
			}